<template>
  <div>
    <div class="card pagination-margin" :class="type">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h5 qid="validations-title" class="mb-2">{{ $t('validations.title') }}</h5>
            <span v-if="getGuide(activeGuide)">
              <GuideButton :guide="getGuide(activeGuide)"></GuideButton>
            </span>
          </div>
          <div class="col-sm-6">
            <span v-if="permission && isGranted([permissions[permission[0]], permissions[permission[1]]], object)">
              <button class="btn btn-light float-right"
                      :class="'btn-' + (createButton ? createButton : 'light')"
                      @click="showCreateValidationModal"
                      :id="'validate_' + type"
                      v-if="!getClient.parent || (getClient.parent && object.partition)"
                      :qid="'validations-create-button' + type"
              >
                <font-awesome-icon icon="check-double"/> {{ $t('validations.create.button_label') }}
              </button>
            </span>
            <span v-if="!permission">
              <button class="btn btn-light float-right"
                      :class="'btn-' + (createButton ? createButton : 'light')"
                      @click="showCreateValidationModal"
                      v-if="!getClient.parent || (getClient.parent && object.partition)"
                      :qid="'validations-create-button' + type"
              >
                <font-awesome-icon icon="check-double"/> {{ $t('validations.create.button_label') }}
              </button>
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
            <tr>
              <th scope="col" class="cursor-pointer">{{ $t('validations.create.status_label') }}</th>
              <th scope="col" class="cursor-pointer">{{ $t('validations.create.owner_label') }}</th>
              <th scope="col" class="cursor-pointer">{{ $t('validations.create.comment_label') }}</th>
              <th scope="col" class="cursor-pointer">{{ $t('validations.create.date_label') }}</th>
              <th width="100"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="validation in tableItems" qid="validations-list-item">
              <td>
                <span v-if="validation.validation === 1" qid="validations-status-approved" class="text-normal">
                  <font-awesome-icon icon="check-circle" class="icon-success"/>
                  {{ $t('system.approved') }}
                </span>
                <span v-if="validation.validation === 0" qid="validations-status-rejected" class="text-normal">
                  <font-awesome-icon icon="times-circle" class="icon-danger"/>
                  {{ $t('system.rejected') }}
                </span>
              </td>
              <td>
                <img v-if="validation.updated_by"
                     v-bind:src="getUserAvatar(validation.updated_by)"
                     class="rounded-circle mr-3"
                     width="30"
                     height="30"
                     qid="profile-photo"
                >
                <span v-if="validation.updated_by" class="text-normal">
                  {{ validation.updated_by.name }}
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ validation.comment | truncate(50)}}
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ validation.created_at | formatDate }}
                </span>
              </td>
              <td align="right">
                <button @click="showViewModal(validation)"
                        class="btn btn-view"
                        qid="validations-view-modal"
                >
                  <font-awesome-icon icon="eye" />
                </button>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card-body">
        <div class="pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete validation confirm modal template -->
    <b-modal ref="delete-validation" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-validation-title">
          {{ $t('validations.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('validations.delete.description') }}
      </h5>
      <h5 class="text-center">
        {{ $t('validations.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-validation-cancel-button"
        >
          {{ $t('validations.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteValidation(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-validation-submit-button"
        >
          {{ $t('validations.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- View validation modal template -->
    <b-modal ref="view-validation" hide-footer @hide="hideViewModal">
      <div class="d-block text-center">
        <h4 qid="delete-validation-title">
          {{ $t('validations.view.title') }}
        </h4>
      </div>
      <div class="d-block">
        <label qid="create-validation-status-label" class="mt-3">
          {{ $t('validations.create.status_label') }}
        </label>
        <br>
        <span v-if="itemToView.validation === 1">
          <font-awesome-icon icon="check-circle" class="icon-success"/>
          {{ $t('system.approved') }}
        </span>
        <span v-if="itemToView.validation === 0">
          <font-awesome-icon icon="times-circle" class="icon-danger"/>
          {{ $t('system.rejected') }}
        </span>
        <br>
        <label qid="create-validation-comment-label" class="mt-4">
          {{ $t('validations.create.comment_label') }}
        </label>
        <br>
        <span class="text-formatted">
          {{ itemToView.comment }}
        </span>
        <br>
        <b-form-group class="mt-2" v-if="!_.isEmpty(itemToView.checklist)">
          <label qid="create-validation-checklist-label" class="mt-3">
            {{ $t('validations.create.checklist_label') }}
          </label>
          <div class="row" v-if="itemToView.checklist">
            <div v-for="(value,key) in itemToView.checklist" class="col-sm-12">
              <p class="mt-2">
                {{ $t('validations.checklist.' + $options.filters.slugify(key)) }}
              </p>
              <div v-for="(itemValue,itemKey) in value" class="row">
                <div class="col-sm-6">
                  <p class="text-normal smaller">
                    {{ $t('validations.checklist.' + $options.filters.slugify(itemKey)) }}
                  </p>
                </div>
                <div class="col-sm-6 text-right">
                  {{ $t('system.' + $options.filters.slugify(itemValue)) }}
                </div>
              </div>
            </div>
          </div>
        </b-form-group>
        <b-form-group v-if="itemToView.media && itemToView.media.length">
          <label qid="create-validation-media-label" class="mt-3">
            {{ $t('validations.create.attachments_label') }}
          </label>
          <p v-for="item in itemToView.media">
            <span class="float-right text-muted cursor-pointer"
                  @click="downloadMedia(item)"
                  qid="download-media"
            >
              <font-awesome-icon icon="cloud-download-alt" />
            </span>
            <a @click="downloadMedia(item)" qid="download-media-button">
              {{ item.file_name }}
            </a>
          </p>
        </b-form-group>
        <b-form-group v-if="itemToView.links && itemToView.links.length">
          <label qid="create-validation-links-label" class="mt-3">
            {{ $t('validations.create.links_label') }}
          </label>
          <div v-if="itemToView.links">
            <p v-for="link in itemToView.links">
              <a :href="link.url" target="_blank" qid="create-tp-link-item">
                {{ link.name ? link.name : link.url }}
              </a>
            </p>
          </div>
        </b-form-group>
      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="success"
                  @click="hideViewModal"
                  qid="view-validation-cancel-button"
        >
          {{ $t('validations.view.close') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Validation create modal template -->
    <b-modal ref="create-validation" hide-footer>
      <div class="d-block text-center">
        <h4 qid="create-validation-title">
          {{ $t('validations.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-validation-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="createValidationFormSubmit" qid="create-validation-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-validation-status-label">
              {{ $t('validations.create.status_label') }}
            </label>
            <b-form-radio-group
              v-model="validationForm.validation"
              :options="validationOptions"
              :required="true"
              qid="create-validation-status"
            ></b-form-radio-group>
          </b-form-group>
          <b-form-group>
            <label qid="create-validation-comment-label">
              {{ $t('validations.create.comment_label') }}
            </label>
            <b-form-textarea
              qid="create-validation-comment"
              v-model="validationForm.comment"
              rows="6"
              :required="true"
              max-rows="6"
              :placeholder="$t('validations.create.comment_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group v-if="checklist">
            <label qid="create-validation-checklist-label">
              {{ $t('validations.create.checklist_label') }}
            </label>
            <br>
            <div v-for="(value,key) in checklist">
              <p class="mt-2">
                {{ $t('validations.checklist.' + $options.filters.slugify(key)) }}
              </p>
              <div v-for="item in value" class="row">
                <div class="col-sm-6">
                  <p class="text-normal smaller" :class="{'text-muted': disabledArray && disabledArray.indexOf(item.title) > -1}">
                    {{ $t('validations.checklist.' + $options.filters.slugify(item.title)) }}
                  </p>
                </div>
                <div class="col-sm-6 text-right" v-if="validationForm.checklist[key]">
                  <b-form-radio-group
                    :disabled="disabledArray && disabledArray.indexOf(item.title) > -1"
                    v-model="validationForm.checklist[key][item.title]"
                    :options="checklistOptions"
                    qid="create-validation-appropriate-measures"
                  ></b-form-radio-group>
                </div>
              </div>
            </div>
          </b-form-group>
          <b-form-group>
            <label qid="create-tp-attachments-label">
              {{ $t('validations.create.attachments_label') }}
              &nbsp;
              <span class="text-muted">({{ $t('attachments.file_size_label') }})</span>
            </label>
            <br>
            <p v-for="file in getFiles" :key="file.name">{{ file.name }}</p>
            <label class="btn btn-secondary cursor-pointer" qid="validations-attachments-button">
              <input type="file"
                     name="fileupload"
                     value="fileupload"
                     multiple
                     id="file-validation"
                     @change="onFileChange"
                     @click="resetFile"
              >
              <span>
                <font-awesome-icon icon="cloud-upload-alt" />
                {{ $t('system.upload') }}
              </span>
            </label>
          </b-form-group>
          <b-form-group>
            <label qid="validations-link-label">
              {{ $t('validations.create.links_label') }}
            </label>
            <p v-for="(link, index) in validationForm.links">
              <span class="float-right text-muted cursor-pointer"
                    @click="removeLinkItem(index)"
                    qid="validations-link-remove"
              >
                <font-awesome-icon icon="trash-alt" />
              </span>
              <a :href="link.url" target="_blank" qid="create-p-link-item">{{ link.name ? link.name : link.url }}</a>
            </p>
            <div>
              <label class="mt-2">{{ $t('system.link') }}</label>
              <b-form-input
                type="text"
                aria-describedby="inputGroup"
                v-model="inputLink"
                :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
                qid="create-p-links"
              ></b-form-input>
              <label class="mt-2">{{ $t('table.name') }}</label>
              <b-form-input
                type="text"
                aria-describedby="inputGroup"
                v-model="inputLinkName"
                :placeholder="inputLink ? inputLink : $t('table.name')"
                qid="create-p-links"
              ></b-form-input>
            </div>
            <span class="btn btn-link cursor-pointer float-right"
                  @click="handleLink"
                  qid="validations-add-link"
            >
              <font-awesome-icon icon="plus"/> {{ $t('system.add_link') }}
            </span>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateValidationModal"
                    qid="edit-validation-cancel-button"
          >
            {{ $t('validations.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-validation-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('validations.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import {initialsAvatarUrl} from '@/util/userAvatar.js';
  import piincHttp from '@/util/configureAxios.js'
  import {config} from '@/config.js'
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Validations',
    props: {
      object: {
        type: Object,
        required: true
      },
      type: {
        type: String,
        required: false
      },
      checklist: {
        type: [Array, Object, Boolean]
      },
      disabledItems: {
        type: Array
      },
      permission: {
        type: [Array, String]
      },
      createButton: {
        type: String
      },
      activeGuide: {
        type: String
      }
    },
    data: function() {
      return {
        tableItems: [],
        currentPage: 1,
        totalRows: 0,
        itemToDelete: {},
        itemToView: {},
        validationForm: {
          validation: '',
          comment: '',
          links: [],
          checklist: {}
        },
        validationOptions: [
          {text: this.$t('validations.create.status_accepted'), value: true},
          {text: this.$t('validations.create.status_rejected'), value: false},
        ],
        checklistOptions: [
          {text: this.$t('system.yes'), value: 'Yes'},
          {text: this.$t('system.no'), value: 'No'},
          {text: this.$t('system.n_a'), value: 'N/A'},
        ],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        error: '',
        selectedFiles: [],
        inputLink: '',
        inputLinkName: '',
        loading: true,
        buttonDisabled: false,
        disabledArray: [],
        myGuides: []
      }
    },
    components: {
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFiles() {
        return this.selectedFiles
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " of " + selectedOf
      }
    },
    watch: {
      // Watch when prop changes and then load validations
      object: function (value) {
        this.loadValidations()
      },
      checklist: function (options) {
        if (options) {
          // Set all checklist sections to empty objects
          // We need this so we can later dynamically assign models in multidimensional array
          for (var option in options) {
            this.validationForm.checklist[option] = {}
          }
        }
      },
      disabledItems: function (items) {
        this.disabledArray = items
      }
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.tasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      loadValidations(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (this.object && this.object.id) {
          piincHttp.get('validations', { params:
            {
              page: pageNumber,
              per_page: perPageNumber,
              model_type: this.object.who_am_i,
              model_id: this.object.id,
              type: this.type,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.loading = false;
            self.tableItems = response.data.items;
            // Set number of total items
            self.totalRows = response.data.total_count;
            // Emit event to parent component to update newest validation
            self.$emit('updated', response.data.items[0]);
          }, function() {});
        }
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadValidations(page)
      },
      perPageChanged(perPage) {
        let self = this;

        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
        this.loadValidations(1, perPage.value)
      },
      perPageRemoved() {
        let self = this;
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadValidations(1, self.selectedPerPage)
      },
      resetFile() {
        let imagefile = document.querySelector('#file-validation');
        imagefile.value = '';
      },
      onFileChange() {
        let files = document.querySelector('#file-validation');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      handleLink() {
        // If there's a link in input, add it to links array
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.validationForm.links.push({url: this.inputLink, name: this.inputLinkName})
          // Reset link input form
          this.inputLink = ''
          this.inputLinkName = ''
        }
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.validationForm.links.splice(index, 1);
      },
      pageChanged(page) {
        // Load validation when page changed listener is triggered
        this.loadValidations(page)
      },
      confirmDeleteValidation(validation) {
        let self = this;
        piincHttp.delete('validations/' + validation.id).then(function() {
          self.hideConfirmDeleteModal();
          self.loadValidations(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('validations.delete.success'))
        }, function() {});
      },
      createValidationFormSubmit(evt) {
        let formData = new FormData();
        let files = document.querySelector('#file-validation');
        let sizeError = false;

        // Call handle link function to gather link from link input
        this.handleLink()

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        // Append list of attachments to FormData
        for (var i = 0; i < files.files.length; i++) {
          // Equal to 10 MB
        if (files.files[i].size > 10485760) {
          sizeError = true;
        }
          formData.append("files[]", files.files[i]);
        }

        // Append list of links to FormData
        for (var i = 0; i < this.validationForm.links.length; i++) {
          formData.append("links[]", JSON.stringify(this.validationForm.links[i]));
        }

        formData.append("validation", this.validationForm.validation ? 1 : 0)
        formData.append("comment", this.validationForm.comment)
        formData.append("model_type", this.object.who_am_i)
        formData.append("model_id", this.object.id)

        if (this.validationForm.checklist) {
          formData.append("checklist", JSON.stringify(this.validationForm.checklist))
        }

        if (this.type) {
          formData.append("type", this.type)
        }

        if (sizeError) {
          this.error = this.$t('system.file_size')
        } else {
          this.buttonDisabled = true;
          piincHttp.post('validations', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function() {
            self.hideCreateValidationModal();
            self.loadValidations(self.currentPage);
            self.buttonDisabled = false;
            self.$toastr('success', self.$t('validations.create.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              if (error.data.errors['files.0']) {
                self.error += ' ' + error.data.errors['files.0'][0]
              }
            }
          })
        }
      },
      showCreateValidationModal(object) {
        this.validationForm.validation = '';
        this.validationForm.comment = '';
        if (this.checklist) {
          // Set all checklist sections to empty objects
          // We need this so we can later dynamically assign models in multidimensional array
          for (var option in this.checklist) {
            this.validationForm.checklist[option] = {}
          }
        }
        this.validationForm.links = [];
        this.error = '';
        this.selectedFiles = [];
        this.$refs['create-validation'].show()
      },
      hideCreateValidationModal() {
        this.$refs['create-validation'].hide()
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-validation'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {};
        this.$refs['delete-validation'].hide()
      },
      showViewModal(object) {
        this.itemToView = object
        if (object.checklist) {
          // Try to create object from string, if checklist is string
          try {
            this.itemToView.checklist = JSON.parse(object.checklist)
          } catch (e) {}
        }
        // If already object, just proceed to open the modal
        this.$refs['view-validation'].show()
      },
      hideViewModal() {
        this.itemToView = {};
        this.$refs['view-validation'].hide()
      },
      getUserAvatar(user){
        if (user && user.avatar) {
          return user.avatar;
        } else {
          return initialsAvatarUrl(user);
        }
      },
    },
    created() {
      if (this.getClient && this.getClient.slug) {
        this.loadValidations()
      }

      this.loadGuides(this.activeGuide);

      if (this.type === 'none') {
        this.validationForm.checklist = null;
      }

    }
  }
</script>
